import { RxCross2 } from 'react-icons/rx';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState, type JSX } from 'react';
import backButtonDark from '@/assets/svg/static/back-button-dark.svg';
import styles from './ActiveSearchBar.module.css';
import type { ActiveSearchBarProps } from './ActiveSearchBar.types';

const SEARCH_PLACEHOLDER = 'Search';
const CROSS_ICON_SIZE = 20;

const ActiveSearchBar = ({
  placeholder = SEARCH_PLACEHOLDER,
  initialSearchText,
  className,
  activeInitially = false,
  handleSubmitParent,
}: ActiveSearchBarProps): JSX.Element => {
  const [searchText, setSearchText] = useState(initialSearchText ?? '');
  const [isFocused, setIsFocused] = useState(activeInitially);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const createCurationFlag = params.has('createCurations');

  useEffect(() => {
    setSearchText((prev) => {
      if (!prev) {
        return initialSearchText ?? '';
      } else return prev;
    });
  }, [initialSearchText]);

  const handleSubmit = (query: string): void => {
    if (handleSubmitParent) {
      handleSubmitParent(query);
      return;
    }
    navigate(
      `/search?query=${query}${createCurationFlag ? '&createCurations' : ''}`
    );
  };

  useEffect(() => {
    if (!activeInitially) return;
    inputRef.current?.focus();
  }, [activeInitially]);

  return (
    <div className={`${styles.searchContainer} ${className}`}>
      <div className={styles.backButtonContainer}>
        <img
          className={styles.backButton}
          src={backButtonDark}
          alt="Back"
          onClick={() => navigate(-1)}
        />
      </div>
      <form
        className={styles.searchForm}
        onSubmit={(e) => {
          e.preventDefault();
          if (searchText.trim()) {
            handleSubmit(encodeURIComponent(searchText.trim()));
          }
        }}
      >
        <input
          type="text"
          name="search query"
          className={styles.searchBar}
          placeholder={placeholder}
          ref={inputRef}
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
          }}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        />
        {isFocused && searchText && (
          <div
            className={styles.crossIconContainer}
            onMouseDown={(e) => {
              e.preventDefault();
              setSearchText('');
            }}
          >
            <RxCross2 className={styles.crossIcon} size={CROSS_ICON_SIZE} />
          </div>
        )}
      </form>
    </div>
  );
};

export default ActiveSearchBar;
